import { ButtonBase, makeStyles } from '@material-ui/core';
import { memo } from 'react';

import { useUser } from '../hooks/useUser';
import { IconType } from '../modules/data';
import { FirstCallType, ListCarDTO, SourceType } from '../modules/generated/api';
import { assessmentDocumentTypes, getLabel } from '../modules/labels';
import { openAuthenticatedPdf } from '../modules/pdf-helpers';
import WithChildren from '../types/WithChildren';

import { filterHighlightPromotions } from './cars-table-next/columns/lib';
import HighlightIcon from './HighlightIcon';

type HighlightsProps = {
  car: ListCarDTO;
};

const useStyles = makeStyles(({ spacing }) => ({
  highlightsWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    minWidth: spacing(12.5),
  },
}));

const Highlights = ({
  car: {
    colorId,
    countryOrigin,
    equipmentKeys,
    assessmentDocuments,
    color,
    firstCallType,
    virtualSources,
    promotions,
    warrantyInformation,
  },
  children,
}: WithChildren<HighlightsProps>) => {
  const { data: user } = useUser();
  const classes = useStyles();

  return (
    <div className={classes.highlightsWrapper}>
      {(
        [
          ['PRIMARY', assessmentDocuments?.primary ? [assessmentDocuments.primary] : []],
          ['WORKSHOP_REPORT', assessmentDocuments?.workshopReports ?? []],
          [
            'PRIOR_DAMAGE_REPORT',
            assessmentDocuments?.priorDamageReport ? [assessmentDocuments.priorDamageReport] : [],
          ],
          ['OTHER', assessmentDocuments?.others ?? []],
        ] as const
      ).flatMap(([type, urls]) =>
        urls.map((url, index) => (
          <ButtonBase key={url} onClick={() => openAuthenticatedPdf(url)}>
            <HighlightIcon
              type={IconType.ASSESSMENT}
              value={urls.length > 1 ? String(index + 1) : 'default'}
              label={getLabel(assessmentDocumentTypes, type)}
            />
          </ButtonBase>
        )),
      )}
      {colorId && <HighlightIcon type={IconType.COLOR} value={colorId} label={color} />}
      <HighlightIcon type={IconType.COUNTRY} value={countryOrigin ?? 'EU'} />
      {virtualSources?.includes(SourceType.Vvj) && (
        <HighlightIcon type={IconType.VIRTUAL_SOURCE} value={SourceType.Vvj} />
      )}
      {firstCallType && (
        <HighlightIcon
          type={IconType.VIRTUAL_SOURCE}
          value={firstCallType === FirstCallType.Own ? SourceType.RealFirstCallOwn : SourceType.RealFirstCallOther}
        />
      )}
      {filterHighlightPromotions(promotions, user?.dealer?.country).map(({ key, promotion, highlighted }) => (
        <HighlightIcon
          key={key}
          type={IconType.PROMOTION}
          value={promotion}
          color={highlighted ? 'yellow' : undefined}
        />
      ))}
      {(equipmentKeys || []).map((equipment: string) => (
        <HighlightIcon key={equipment} type={IconType.EQUIPMENT} value={equipment} warrantyInfo={warrantyInformation} />
      ))}
      {children}
      {promotions?.includes('THERMO') && <HighlightIcon type={IconType.SPECIAL} value="THERMO" />}
    </div>
  );
};

export default memo(Highlights);
